import {CommentBox, type CommentBoxHandle} from '@github-ui/comment-box/CommentBox'
import type {Subject} from '@github-ui/comment-box/subject'
import type {ValidationResult} from '@github-ui/entity-validators'
import {validateComment} from '@github-ui/entity-validators'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {canBeOptimisticallyParsed} from '@github-ui/md2html'
import {noop} from '@github-ui/noop'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {testIdProps} from '@github-ui/test-id-props'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {graphql, useRelayEnvironment} from 'react-relay'
import {useFragment} from 'react-relay/hooks'

import {BUTTON_LABELS} from '../../constants/button-labels'
import {ERRORS} from '../../constants/errors'
import {LABELS} from '../../constants/labels'
import {TEST_IDS} from '../../constants/test-ids'
import type {IssueCommentEditor_repository$key} from './__generated__/IssueCommentEditor_repository.graphql'
import type {IssueCommentEditorBodyFragment$key} from './__generated__/IssueCommentEditorBodyFragment.graphql'
import styles from './IssueCommentEditor.module.css'
import {IssueCommentHeader} from './IssueCommentHeader'
import type {updateIssueCommentBodyMutation$data} from './mutations/__generated__/updateIssueCommentBodyMutation.graphql'
import {commitUpdateIssueCommentBodyMutation} from './mutations/update-issue-comment-body-mutation'

type Props = {
  initialValue: string
  comment: IssueCommentEditorBodyFragment$key
  repo: IssueCommentEditor_repository$key
  cancelEdit: () => void
  onSave: () => void
  onChange: (value: string) => void
  setIsEditing: (isEditing: boolean) => void
  setIsSubmitting: (isSubmitting: boolean) => void
  setHasDisabledActions: (hasDisabledActions: boolean) => void
  isSubmitting: boolean
  initialValidationResult?: ValidationResult
  setManualValidationResult?: (result: ValidationResult | undefined) => void
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const IssueCommentEditorFragment = graphql`
  fragment IssueCommentEditor_repository on IssueComment {
    repository {
      slashCommandsEnabled
      nameWithOwner
      databaseId
    }
  }
`

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const IssueCommentEditorBodyFragment = graphql`
  fragment IssueCommentEditorBodyFragment on IssueComment {
    id
    body
    bodyVersion
    author {
      login
      avatarUrl
    }
    issue {
      id
      databaseId
      author {
        login
      }
    }
    ...IssueCommentHeader
  }
`

export function IssueCommentEditor({
  initialValue = '',
  comment,
  repo,
  cancelEdit,
  onSave,
  onChange,
  setIsEditing,
  setHasDisabledActions,
  isSubmitting,
  setIsSubmitting,
  initialValidationResult,
  setManualValidationResult,
}: Props) {
  const ref = useRef<CommentBoxHandle>(null)
  const data = useFragment(IssueCommentEditorBodyFragment, comment)
  const {body, bodyVersion} = data
  const commentRepository = useFragment<IssueCommentEditor_repository$key>(IssueCommentEditorFragment, repo)
  const {repository} = commentRepository

  const repositoryDatabaseId = repository.databaseId!
  const environment = useRelayEnvironment()
  const {addToast} = useToastContext()
  const [currentBody, setCurrentBody] = useState<string>(initialValue || body)
  const [currentBodyVersion] = useState<string>(bodyVersion)
  const computedValidationResult = useMemo(() => validateComment(currentBody), [currentBody])

  const onMarkdownChange = (newMarkdown: string) => {
    setCurrentBody(newMarkdown)
    onChange(newMarkdown)
  }

  const save = useCallback(
    (newId: string) => {
      const withOptimisticResponse = canBeOptimisticallyParsed(currentBody)
      if (withOptimisticResponse) {
        setIsEditing(false)
        setHasDisabledActions(true)
      } else {
        setIsSubmitting(true)
      }
      commitUpdateIssueCommentBodyMutation({
        environment,
        input: {id: newId, body: currentBody, bodyVersion: currentBodyVersion},
        onError: (error: Error) => {
          setIsSubmitting(false)
          setHasDisabledActions(false)

          // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
          addToast({
            type: 'error',
            message: error.message.startsWith(LABELS.staleCommentErrorPrefix)
              ? ERRORS.couldNotEditCommentStale
              : ERRORS.couldNotEditComment,
          })
          // this distinction is because for this specific error we don't want to close the editor (so the user can first copy the changes)
          if (!error.message.startsWith(LABELS.staleCommentErrorPrefix)) {
            onSave()
          } else {
            setManualValidationResult?.({
              isValid: false,
              errorMessage: ERRORS.couldNotEditCommentStale,
            })
          }
        },
        onCompleted: (response?: updateIssueCommentBodyMutation$data) => {
          setIsSubmitting(false)
          setHasDisabledActions(false)
          if (!response?.updateIssueComment) {
            // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
            addToast({
              type: 'error',
              message: ERRORS.couldNotEditComment,
            })
          } else {
            onSave()
          }
        },
        withOptimisticResponse,
      })
    },
    [
      currentBody,
      environment,
      currentBodyVersion,
      setIsEditing,
      setHasDisabledActions,
      setIsSubmitting,
      addToast,
      onSave,
      setManualValidationResult,
    ],
  )

  const onCancel = () => {
    cancelEdit()
  }

  useEffect(() => {
    if (ref && ref.current) {
      ref.current.focus()
    }
  }, [ref])

  const subject = useMemo<Subject>(() => {
    return {
      type: 'issue_comment',
      id: {
        id: data.issue.id,
        databaseId: data.issue.databaseId!,
      },
      repository: {
        databaseId: repositoryDatabaseId,
        nwo: repository.nameWithOwner,
        slashCommandsEnabled: repository.slashCommandsEnabled,
      },
    }
  }, [
    data.issue.databaseId,
    data.issue.id,
    repository.nameWithOwner,
    repository.slashCommandsEnabled,
    repositoryDatabaseId,
  ])

  const issuesReactLazyLoad = isFeatureEnabled('issues_lazy_load_comment_box_suggestions')

  return (
    <>
      <IssueCommentHeader
        comment={data}
        commentAuthorLogin={data.author!.login}
        avatarUrl={data.author!.avatarUrl}
        commentSubjectAuthorLogin={data.issue.author?.login}
        editComment={noop}
        onReplySelect={noop}
        navigate={noop}
        hideActions
      />
      <div className={styles.EditorMarginContainer}>
        <CommentBox
          disabled={isSubmitting}
          subject={subject}
          ref={ref}
          /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- TODO: [SECURITY] Potential XSS risk. Pre-existing infraction - DO NOT COPY. Must be fixed. */
          value={(initialValue || currentBody) as SafeHTMLString}
          placeholder=""
          onSave={() => save(data.id)}
          onCancel={onCancel}
          validationResult={initialValidationResult ? initialValidationResult : computedValidationResult}
          onChange={onMarkdownChange}
          saveButtonText={BUTTON_LABELS.updateComment}
          saveButtonTrailingIcon={!isSubmitting}
          buttonSize="medium"
          teamHovercardsEnabled
          markdownSuggestionsFetchMethod={issuesReactLazyLoad ? 'lazy' : 'eager'}
          className={styles.CommentBox}
          {...testIdProps(TEST_IDS.commentBox('comment-editor'))}
        />
      </div>
    </>
  )
}
